import { useEffect, useState } from "react";
import { useSafeApiCall, useApiClient } from "src/hooks/api";
import { useAuth } from "src/context";
import { API_PREFIX_PATH } from "src/constants";
import { downloadDocument } from "src/utils/documentDownload";
import { getSkipLosValue } from "src/utils";
import { createIsoDate } from "../../utils";
import { zeroDate } from "../../constants";
import { cleanPhoneNumber } from "src/utils/phone";
import {
  type Dealer,
  type ActivityLog,
  type DealerDocument,
  type UpdateDealerDocumentsReq,
  type DealerDocumentType,
  type NoteRequest,
} from "src/vibe-code/rcm-v2/types";

export const useDealers = () => {
  const [dealers, setDealers] = useState<Dealer[]>([]);
  const { fetchFromApi } = useApiClient();
  const safeApiCall = useSafeApiCall();

  const getDealers = () => {
    safeApiCall(() => fetchFromApi(`${API_PREFIX_PATH}/dealers`), {
      suppressErrorRedirect: true,
    })
      .then((response) => {
        setDealers(response as Dealer[]);
      })
      .catch((err) => console.log(err));
  };

  const updateDealer = (data: any) => {
    let formatted_onboarding_date = zeroDate;
    if (data.onboarding_date) {
      const [year, month, day] = data.onboarding_date.split("-");
      formatted_onboarding_date = createIsoDate(year, month, day);
    }
    let formatted_last_visit_date = zeroDate;
    if (data.last_visit_date) {
      const [year, month, day] = data.last_visit_date.split("-");
      formatted_last_visit_date = createIsoDate(year, month, day);
    }
    const cleanedData = {
      street1: data.street1,
      street2: data.street2,
      city: data.city,
      state: data.state,
      zip_code: data.zip_code,
      name: data.name,
      dealer_feedback: data.dealer_feedback,
      new_dealer_id: data.external_id,
      dealer_type: data.dealer_type,
      email: data.email,
      lenders: data.lenders,
      store_number: data.store_number,
      onboarding_date: formatted_onboarding_date,
      last_visit_date: formatted_last_visit_date,
      phone_number: cleanPhoneNumber(data.phone_number),
      uses_dla: data.uses_dla,
      vep_code: data.vep_code,
      website_url: data.website_url,
      skip_los: getSkipLosValue(),
    };

    if (cleanedData.website_url.length === 0) {
      delete cleanedData.website_url;
    }

    safeApiCall(
      () =>
        fetchFromApi(`${API_PREFIX_PATH}/dealers/${data.id}`, {
          method: "PUT",
          body: cleanedData,
        }),
      {
        suppressErrorRedirect: true,
        useServerErrorAsToast: true,
      },
    )
      .then(() => getDealers())
      .catch((err) => console.log(err));
  };
  const createDealer = (data: any) => {
    let formatted_onboarding_date = zeroDate;
    if (data.onboarding_date) {
      const [year, month, day] = data.onboarding_date.split("-");
      formatted_onboarding_date = createIsoDate(year, month, day);
    }
    let formatted_last_visit_date = zeroDate;
    if (data.last_visit_date) {
      const [year, month, day] = data.last_visit_date.split("-");
      formatted_last_visit_date = createIsoDate(year, month, day);
    }
    const cleanedData = {
      street1: data.street1,
      street2: data.street2,
      city: data.city,
      state: data.state,
      zip_code: data.zip_code,
      name: data.name,
      dealer_feedback: data.dealer_feedback,
      dealer_id: data.external_id,
      dealer_type: data.dealer_type,
      email: data.email,
      lenders: data.lenders,
      store_number: data.store_number,
      onboarding_date: formatted_onboarding_date,
      last_visit_date: formatted_last_visit_date,
      phone_number: cleanPhoneNumber(data.phone_number),
      uses_dla: data.uses_dla,
      vep_code: data.vep_code,
      website_url: data.website_url,
      skip_los: getSkipLosValue(),
    };

    if (cleanedData.website_url?.length === 0) {
      delete cleanedData.website_url;
    }

    safeApiCall(
      () =>
        fetchFromApi(`${API_PREFIX_PATH}/dealers`, {
          method: "POST",
          body: cleanedData,
        }),
      {
        suppressErrorRedirect: true,
        useServerErrorAsToast: true,
      },
    )
      .then(() => getDealers())
      .catch((err) => console.log(err));
  };

  useEffect(() => {
    getDealers();
  }, []);

  return {
    dealers,
    getDealers,
    updateDealer,
    createDealer,
  };
};

export const useDealerDetails = () => {
  const { fetchFromApi } = useApiClient();
  const { user } = useAuth();
  const safeApiCall = useSafeApiCall();

  const getDealerActivityLogs = async (
    dealerId: string,
  ): Promise<ActivityLog[]> => {
    try {
      const response = await safeApiCall(
        () =>
          fetchFromApi(`${API_PREFIX_PATH}/dealers/${dealerId}/activity-logs`),
        { suppressErrorRedirect: true },
      );
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Error fetching dealer activity logs:", error);
      return [];
    }
  };

  const getDealerDocuments = async (
    dealerId: string,
  ): Promise<DealerDocument[]> => {
    try {
      const response = await safeApiCall(
        () => fetchFromApi(`${API_PREFIX_PATH}/dealers/${dealerId}/documents`),
        { suppressErrorRedirect: true },
      );
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Error fetching dealer documents:", error);
      return [];
    }
  };

  const createDealerNote = async (
    dealerId: string,
    noteRequest: NoteRequest,
  ): Promise<boolean> => {
    try {
      await safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/dealers/${dealerId}/activity-logs/note`,
            {
              method: "POST",
              body: noteRequest,
            },
          ),
        {
          suppressErrorRedirect: true,
          useServerErrorAsToast: true,
        },
      );
      return true;
    } catch (error) {
      console.error("Error creating dealer note:", error);
      return false;
    }
  };

  const updateDealerDocuments = async (
    dealerId: string,
    documentsRequest: UpdateDealerDocumentsReq,
  ): Promise<DealerDocument[]> => {
    try {
      const response = await safeApiCall(
        () =>
          fetchFromApi(`${API_PREFIX_PATH}/dealers/${dealerId}/documents`, {
            method: "PUT",
            body: documentsRequest,
          }),
        { suppressErrorRedirect: true },
      );
      return Array.isArray(response) ? response : [];
    } catch (error) {
      console.error("Error updating dealer documents:", error);
      return [];
    }
  };

  const downloadDealerDocument = async (
    dealerId: string,
    documentType: DealerDocumentType,
  ): Promise<boolean> => {
    try {
      await downloadDocument(
        fetchFromApi,
        safeApiCall,
        `${API_PREFIX_PATH}/dealers/${dealerId}/documents/${documentType}`,
        `${documentType}_${dealerId}.pdf`,
      );
      return true;
    } catch (error) {
      console.error("Error downloading dealer document:", error);
      return false;
    }
  };

  const uploadDealerDocument = async (
    dealerId: string,
    documentType: DealerDocumentType,
    file: File,
  ): Promise<boolean> => {
    try {
      // Convert file to byte array as expected by the API
      const arrayBuffer = await file.arrayBuffer();
      const content = Array.from(new Uint8Array(arrayBuffer));

      await safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/dealers/${dealerId}/documents/${documentType}`,
            {
              method: "POST",
              body: {
                content,
                file_name: file.name,
              },
            },
          ),
        {
          suppressErrorRedirect: true,
          useServerErrorAsToast: true,
        },
      );
      return true;
    } catch (error) {
      console.error("Error uploading dealer document:", error);
      return false;
    }
  };

  const removeDealerDocument = async (
    dealerId: string,
    documentType: DealerDocumentType,
  ): Promise<boolean> => {
    try {
      await safeApiCall(
        () =>
          fetchFromApi(
            `${API_PREFIX_PATH}/dealers/${dealerId}/documents/${documentType}`,
            {
              method: "DELETE",
            },
          ),
        {
          suppressErrorRedirect: true,
          useServerErrorAsToast: true,
        },
      );
      return true;
    } catch (error) {
      console.error("Error removing dealer document:", error);
      return false;
    }
  };

  return {
    getDealerActivityLogs,
    getDealerDocuments,
    createDealerNote,
    updateDealerDocuments,
    downloadDealerDocument,
    uploadDealerDocument,
    removeDealerDocument,
  };
};
